<script>
import BaseView from "@/mixins/BaseView.vue";
import GoogleMapsMixin from "@/mixins/google-maps.mixin";
import { Marker as GMarker, Polyline as GPolyline } from "vue3-google-map";
import OmniGoogleMap from "@/components/omni/OmniGoogleMap.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { OrdersMixin } from "@/views/orders/mixins/orders.mixin.ts";
import { decode } from "@googlemaps/polyline-codec";
import { EndPointMarker, PathArrowReverse, StartPointMarker } from "@/constants/markers.ts";
import OrderMapLegendContent from "@/views/orders/components/OrderMapLegendContent.vue";

export default {
  name: "OrderStopsRoute",
  components: { OrderMapLegendContent, OmniDialog, OmniGoogleMap, GPolyline, GMarker },
  mixins: [BaseView, GoogleMapsMixin, OrdersMixin],
  props: {
    order: {
      type: Object,
    },
  },
  data() {
    return {
      dialogVisible: false,
      center: null,
      zoom: 4,
      markers: [],
      selectedLocationIndex: null,
      listCollapsed: false,
      lineOptions: {
        icons: [
          {
            icon: PathArrowReverse,
            offset: 0,
            repeat: "50px",
          },
        ],
        strokeWeight: 6,
        strokeColor: "#4CAF50",
        path: null,
      },
    };
  },
  computed: {
    totalDistance() {
      return Object.values(this.order.distances).reduce((a, b) => a + b, 0);
    },
  },
  watch: {
    order: {
      async handler() {
        this.resetMap();
        if (this.order && this.order.orderStops) {
          const locations = this.order.orderStops.map((stop) => stop.facility.addressCoordinates);
          this.setCenterAuto(locations);

          try {
            const response = await this.$api.orders.findPolylineByOrderId(this.order.id);
            const polyline = response.data;

            if (!polyline) {
              return;
            }

            const decoded = decode(polyline, 5).map((coords) => this.toLatLng(coords));
            const linePath = decoded ? decoded : locations.map((coords) => this.toLatLng(coords));
            this.lineOptions = {
              ...this.lineOptions,
              path: linePath,
            };
          } catch (error) {
            console.error(error);
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    getFacilityMarker(stop) {
      return {
        label:
          stop.type === this.STOP_PICK_UP
            ? {
                text: "Pick up",
                className: "map__label start",
              }
            : {
                text: "Delivery",
                className: "map__label end",
              },
        position: this.toLatLng(stop.facility.addressCoordinates),
        icon: stop.type === this.STOP_PICK_UP ? StartPointMarker : EndPointMarker,
      };
    },
    toLatLng(coords) {
      if (Array.isArray(coords)) {
        return { lat: coords[0], lng: coords[1] };
      }
      return { lat: coords.lat, lng: coords.lon };
    },
    setCenterAuto(locations) {
      let latSum = 0;
      let lonSum = 0;
      locations.forEach((location) => {
        if (location) {
          latSum += location.lat;
          lonSum += location.lon;
        }
      });
      this.center = { lat: latSum / locations.length, lng: lonSum / locations.length };
      this.zoom = 6;
    },
    resetMap() {
      this.selectedLocationIndex = null;
      this.markers = [];
      this.lineOptions = {
        ...this.lineOptions,
        path: null,
      };
    },
  },
};
</script>

<template>
  <omni-dialog
    v-model="dialogVisible"
    :hide-actions="true"
    :title="'Order ' + order.number + ' route'"
    height="850"
    primary-action-label="Save"
    width="1600"
  >
    <template #title:append>
      <div class="font-weight-500 font-size-14 text-grey-darken-1 ml-6">
        Loaded miles:
        <span class="text-grey-darken-2" data-qa="route-loaded-miles">{{ $filters.miles(totalDistance) }} miles</span>
      </div>
    </template>
    <v-row class="viewport-container mt-4">
      <v-col v-if="!listCollapsed" :cols="4" class="locations-container pa-0">
        <div class="viewport overflow-auto pt-3 d-block mb-4 pr-3 pl-1">
          <template v-for="(stop, index) in order.orderStops" :key="stop.id">
            <div
              :class="{
                'location__pickup labeled': stop.type === STOP_PICK_UP,
                'location__delivery labeled': stop.type === STOP_DELIVERY,
              }"
              class="pa-4 rounded bg-grey-lighten-4 location"
            >
              <div class="float-end text-right font-size-12 font-weight-500 text-grey-darken-1">Not started</div>
              <div class="mb-2">
                <router-link
                  :to="{ name: 'facilities-edit', params: { id: stop.facility.id } }"
                  class="text-primary font-weight-500 font-size-16 text-decoration-none"
                  data-qa="route-facility-name"
                  target="_blank"
                >
                  {{ stop.facility.name }}
                </router-link>
              </div>
              <div class="mb-5 text-grey-darken-2" data-qa="route-facility-address">{{ stop.facility.address }}</div>
              <div class="d-flex align-center text-grey-darken-1 font-weight-500" data-qa="route-facility-time">
                <v-icon class="mr-2" size="20">mdi-clock-outline</v-icon>
                <div>
                  <div>
                    {{ getTimeType(stop.timeType) }}: {{ getTimeInterval(stop.timeFrom, stop.timeTo, stop.timezone) }}
                  </div>
                  <div v-if="stop.time2To">
                    {{ getTimeType(stop.time2Type) }}:
                    {{ getTimeInterval(stop.time2From, stop.time2To, stop.timezone) }}
                  </div>
                </div>
              </div>
            </div>
            <div v-if="index < order.orderStops.length - 1" class="distance-gap">
              <div class="distance-gap--distance text-grey-darken-1">
                {{ getStopDistance(order.orderStops, order.distances, index) }} miles
              </div>
              <div class="distance-gap--line"></div>
            </div>
          </template>
        </div>
      </v-col>
      <v-col :cols="listCollapsed ? 12 : 8" class="pa-0 pt-3 position-relative">
        <div class="list-collapse" @click="listCollapsed = !listCollapsed">
          <v-icon v-if="!listCollapsed">mdi-menu-left</v-icon>
          <v-icon v-else>mdi-menu-right</v-icon>
        </div>
        <omni-google-map
          :api-key="getApiKey()"
          :center="center"
          :fullscreen-control="false"
          :street-view-control="false"
          :zoom="zoom"
          style="width: 100%; height: 100%"
        >
          <g-polyline :options="lineOptions"></g-polyline>
          <g-marker v-for="stop in order.orderStops" :key="stop.id" :options="getFacilityMarker(stop)"></g-marker>

          <template #mapLegend>
            <order-map-legend-content />
          </template>
        </omni-google-map>
      </v-col>
    </v-row>
  </omni-dialog>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color.scss";

.intro-block {
  margin: 42px auto;
  width: 300px;
  color: $grey;

  &__icon {
    margin: 24px;
    text-align: center;
  }

  &__text {
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    text-align: center;
  }
}

.viewport-container {
  height: calc(100% - 80px);
}

.locations-container {
  .location {
    position: relative;
    cursor: pointer;
    border-left: 4px solid transparent;
    outline-color: transparent;
    transition: outline-color 200ms ease-in-out;
    background: #e0e0e0;

    &.labeled:before {
      text-transform: uppercase;
      font-size: 10px;
      font-weight: 500;
      line-height: 12px;
      padding: 2px 4px;
      border-radius: 4px;
      color: $white;
      position: absolute;
      top: -7px;
      left: 16px;
    }

    &__delivery {
      border-left-color: $danger !important;

      &:before {
        content: "Delivery";
        background-color: $danger;
      }
    }

    &__pickup {
      border-left-color: $green !important;

      &:before {
        content: "Pick up";
        background-color: $green;
      }
    }

    &__address {
      font-size: 14px;
      line-height: 16px;
    }

    &__coordinates {
      display: inline-block;
      font-size: 11px;
      line-height: 16px;
      color: $green;
      text-decoration: underline;
    }

    &__date {
      font-size: 12px;
      line-height: 16px;
    }

    &.active {
      outline: 2px solid $primary !important;
      border-left-color: transparent !important;
    }
  }

  .viewport {
    height: calc(100% - 110px);
  }

  .route-info {
    background-color: $white;
    padding: 24px;
    font-size: 16px;
    color: $dark-grey;

    &__value {
      font-weight: 500;
      white-space: nowrap;
    }
  }
}

.distance-gap {
  position: relative;
  margin: 5px 0;
  height: 12px;

  .distance-gap--distance {
    position: absolute;
    background-color: #ffffff;
    padding: 0 12px;
    top: -5px;
    left: 40%;
    font-size: 10px;
    font-weight: 500;
    z-index: 10;
  }

  .distance-gap--line {
    position: absolute;
    top: 5px;
    height: 1px;
    width: 100%;
    background-color: #e0e0e0;
  }
}

.list-collapse {
  position: absolute;
  z-index: 99;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  background-color: $white;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  padding: 6px 0;
  cursor: pointer;
  box-shadow:
    0 3px 3px -2px rgba(0, 0, 0, 0.2),
    0 3px 4px rgba(0, 0, 0, 0.14),
    0 1px 8px rgba(0, 0, 0, 0.12);
}
</style>
